import React, { useEffect, useState, useRef } from "react";
import type { FC, PropsWithChildren } from "react";
import ReactQuill from "react-quill";
import { renderToStaticMarkup } from "react-dom/server";
import {
  ModalForm,
  Modal,
  Button,
  RadioButton,
  InputsSection,
  ModalButtons,
} from "../../components/Modal";
import ModalHeading from "../../components/Modal/ModalHeading";
import { INote, IPatient } from "../../hooks/patients/index.type";
import classes from "../../styles/CustomStyles.module.css";
import { useDeleteNote } from "../../hooks/patients";
import useNoteTypeOptions, {
  NoteTypeOption,
} from "../../hooks/useNoteTypeOptions";
import CustomDropdown from "../../components/CustomDropdown/CustomDropdown";
import "react-quill/dist/quill.snow.css";
import NoteSkeleton from "../../components/Skeletons/NoteSkeleton";
import toast from "react-hot-toast";
import phoneCallStyles from "../../components/PhoneCallTemplate/PhoneCallTemplate.module.css";

const stripHtmlTags = (html: string) => {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

export type ViewNotesModalState = PropsWithChildren<{
  status: boolean;
  data?: INote;
}>;

interface IAddNoteStates {
  noteCategory: {
    value: "clinical" | "non-clinical" | "timer-summary";
    error: string;
  };
  noteContent: { value: string; error: string };
  selectedNoteId?: string;
}

interface ViewNotesModalProps {
  ViewNotesModal: ViewNotesModalState;
  onCloseModal: () => void;
  handleSaveNote: (
    noteText: string,
    note: INote
  ) => Promise<{ status: string; data?: { note?: Array<INote> } } | undefined>;
  patientId?: string;
  onNoteDeleted?: () => void;
  noteModalStates: IAddNoteStates;
  setNoteModalStates: React.Dispatch<React.SetStateAction<IAddNoteStates>>;
  saveNoteLoading?: boolean;
  fetchedNotes?: INote[];
  getNotesLoading?: boolean;
  getNotes: (reset?: boolean, sortOrder?: "asc" | "desc") => Promise<void>;
  noteTypeOptions: NoteTypeOption[];
  noteTypesLoading?: boolean;
  noteTypesError?: string;
  patientData: IPatient;
  triggeredBy?: string;
}

const styles = {
  iconButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "0.5rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s ease",
    borderRadius: "0.25rem",
    backgroundColor: "transparent",
  },
  iconDelete: {
    width: "1rem",
    height: "1rem",
    fill: "currentColor",
    color: "var(--grey)",
  },
};

const ViewNotesModal: FC<ViewNotesModalProps> = (props) => {
  const [selectedNote, setSelectedNote] = useState<INote | undefined>(
    props.ViewNotesModal.data
  );

  const [isSavingNote, setIsSavingNote] = useState(false);
  const [isUpdatingNote, setIsUpdatingNote] = useState(false);

  useEffect(() => {
    setSelectedNote(props.ViewNotesModal.data);
  }, [props.ViewNotesModal.data]);

  const [noteText, setNoteText] = useState<string>("");
  const [originalNoteText, setOriginalNoteText] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<string>("Latest First");
  const [editableNoteCategory, setEditableNoteCategory] = useState<
    "clinical" | "non-clinical" | "timer-summary"
  >("clinical");
  const [isImportant, setIsImportant] = useState<boolean>(false);
  const [isPhoneCall, setIsPhoneCall] = useState<boolean>(false);
  const [originalIsPhoneCall, setOriginalIsPhoneCall] =
    useState<boolean>(false);
  const [selectedNoteType, setSelectedNoteType] = useState<string>("");
  const [selectedNoteLabel, setSelectedNoteLabel] =
    useState<string>("Select Note Type");
  const [lastUpdatedBy, setLastUpdatedBy] = useState<string>("Admin");
  const [createdBy, setCreatedBy] = useState("");

  // const {
  //   noteTypeOptions,
  //   loading: noteTypesLoading,
  //   error: noteTypesError,
  // } = useNoteTypeOptions();

  useEffect(() => {
    if (props.ViewNotesModal.data) {
      const noteData = props.ViewNotesModal.data;
      setNoteText(noteData.richText || "");
      setOriginalNoteText(noteData.richText || "");
      setEditableNoteCategory(
        (noteData.noteCategory as "clinical" | "non-clinical") || "clinical"
      );
      setIsImportant(!!noteData.isImportant && noteData.isImportant === "yes");
      const phoneCallStatus = noteData.isPhoneCall === "yes";
      setIsPhoneCall(phoneCallStatus);
      setOriginalIsPhoneCall(phoneCallStatus);
      // Set the last updated by clinician name
      const clinicianName =
        noteData.clinicianId?.name ||
        noteData.clinician?.name ||
        props.fetchedNotes?.[0]?.clinicianId?.name ||
        props.fetchedNotes?.[0]?.clinician?.name ||
        "-";
      setLastUpdatedBy(clinicianName);

      //set the created by
      const createdByName = noteData.createdBy?.name;
      setCreatedBy(createdByName);

      if (noteData.type) {
        setSelectedNoteType(noteData.type);
        const noteType = props.noteTypeOptions.find(
          (opt) => opt.key === noteData.type
        );
        if (noteType) {
          setSelectedNoteLabel(noteType.value);
        }
      } else {
        setSelectedNoteType("");
        setSelectedNoteLabel("Select Note Type");
      }
    } else {
      setNoteText("");
      setOriginalNoteText("");
      setEditableNoteCategory("clinical");
      setIsImportant(false);
      setSelectedNoteType("");
      setSelectedNoteLabel("Select Note Type");
    }
  }, [props.ViewNotesModal.data, props.fetchedNotes, props.noteTypeOptions]);
  const [deleteNoteModal, setDeleteNoteModal] = useState<{
    status: boolean;
    data?: INote;
  }>({ status: false });
  const notesContainerRef = useRef<HTMLDivElement>(null);
  const { deleteNote, deleteNoteLoading } = useDeleteNote({
    patientId: props.patientId,
  });

  useEffect(() => {
    if (props.ViewNotesModal.status) {
      setSortOrder("Latest First");
      props.getNotes(true, "desc");
    } else {
      setDeleteNoteModal({ status: false });
    }
  }, [props.ViewNotesModal.status]);

  useEffect(() => {
    if (editableNoteCategory === "non-clinical") {
      setSelectedNoteType("");
      setSelectedNoteLabel("");
    }
  }, [editableNoteCategory]);

  const handleScroll = React.useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

      if (
        scrollHeight - scrollTop <= clientHeight * 1.5 &&
        !props.getNotesLoading
      ) {
        props.getNotes(false, sortOrder === "Latest First" ? "desc" : "asc");
      }
    },
    [props.getNotesLoading, props.getNotes, sortOrder]
  );

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSortOrder(e.target.value);
    const newSortOrder = e.target.value === "Latest First" ? "desc" : "asc";
    props.getNotes(true, newSortOrder);
  };

  const formatDate = (isoString?: string): string => {
    if (!isoString) return "Date not available";
    const date = new Date(isoString);
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "numeric",
    };
    return date.toLocaleDateString("en-US", options);
  };

  const handleSaveNote = async () => {
    if (isSavingNote || !props.ViewNotesModal.data) return;

    try {
      setIsSavingNote(true);

      const noteData: Partial<INote> = {
        noteCategory: editableNoteCategory,
        richText: noteText,
        createdAt: props.ViewNotesModal.data.createdAt,
        isImportant: isImportant ? "yes" : ("no" as const),
        isPhoneCall: isPhoneCall ? "yes" : "no",
      };

      if (editableNoteCategory === "clinical") {
        noteData.type = (selectedNoteType || "general-note") as any;
      }

      // Type the response from handleSaveNote
      const response = await props.handleSaveNote(noteText, noteData as INote);

      // Refresh notes to get the latest data including clinician info
      await props.getNotes(true, "desc");

      // Type the response properly
      type SaveNoteResponse = {
        data?: {
          note?: Array<INote>;
        };
      };

      const typedResponse = response as unknown as SaveNoteResponse;

      // Update the selected note with the latest data including clinician info
      let freshNoteData: INote | undefined;

      // First try to get from the response
      if (typedResponse?.data?.note?.[0]) {
        freshNoteData = typedResponse.data.note[0];
      }
      // Fallback: get the first note from fetched notes (latest note)
      else if (props.fetchedNotes && props.fetchedNotes.length > 0) {
        freshNoteData = props.fetchedNotes[0];
      }

      // Update the selected note and last updated by field
      if (freshNoteData) {
        setSelectedNote(freshNoteData);
        const clinicianName =
          freshNoteData.clinicianId?.name ||
          freshNoteData.clinician?.name ||
          "-";
        setLastUpdatedBy(clinicianName);

        const createdByName = freshNoteData.createdBy?.name;
        setCreatedBy(createdByName);
      }
    } catch (error) {
      console.error("Error saving note:", error);
    } finally {
      setIsSavingNote(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    try {
      const response = await deleteNote(noteId);
      if (response?.status === "success") {
        if (props.getNotes) {
          await props.getNotes(
            true,
            sortOrder === "Latest First" ? "desc" : "asc"
          );
        }

        if (selectedNote?._id === noteId) {
          setSelectedNote(undefined);
          setNoteText("");
          setOriginalNoteText("");
        }

        toast.success("Note deleted successfully");

        if (props.onNoteDeleted) {
          props.onNoteDeleted();
        }
      }
    } catch (error) {
      console.error("Error deleting note:", error);
      toast.error("Failed to delete note");
    }
  };

  const handleDeleteConfirm = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();
    if (deleteNoteModal.data?._id) {
      await handleDeleteNote(deleteNoteModal.data._id);
    }
  };

  const handleClearForm = () => {
    const currentCategory = editableNoteCategory;
    const isNonClinical = currentCategory === "non-clinical";

    setSelectedNote(undefined);
    setNoteText("");
    setOriginalNoteText("");
    setEditableNoteCategory(currentCategory);
    setIsImportant(false);

    if (!isNonClinical) {
      setSelectedNoteType("");
      setSelectedNoteLabel("Select Note Type");
    } else {
      setSelectedNoteType("");
      setSelectedNoteLabel("");
    }

    props.setNoteModalStates((prevVal) => ({
      ...prevVal,
      noteCategory: {
        value: currentCategory,
        error: "",
      },
      noteContent: { value: "", error: "" },
      selectedNoteId: undefined,
    }));
  };

  const handleImportantChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsImportant(e.target.checked);
  };

  const handlePhoneCallChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setIsPhoneCall(isChecked);

    if (isChecked) {
      setOriginalNoteText(noteText);
      const plainText = stripHtmlTags(noteText).trim();

      let noteMessage = plainText
        ? noteText
        : "Reviewing care plan and calling the patient";

      const phoneCallHtml = `
     <div class="${phoneCallStyles.phoneCallContainer}">
      <div class="${phoneCallStyles.phoneCallText}">
        ${noteMessage}
      </div>
      <br/><br/><br/>
      <div class="${phoneCallStyles.divider}">-------</div>
      <div class="${phoneCallStyles.phoneCallHeader}">Phone Call</div>
      <div class="${phoneCallStyles.timeLabel}">Start Time: </div>
      <div class="${phoneCallStyles.timeLabel}">End Time: </div>
      </div>
      `;

      setNoteText(phoneCallHtml);
    } else {
      setNoteText(originalNoteText);
      setOriginalNoteText(originalNoteText);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {};

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (selectedNote && props.fetchedNotes && props.fetchedNotes.length > 0) {
      const updatedNote = props.fetchedNotes.find(
        (note) => note._id === selectedNote._id
      );
      if (updatedNote) {
        setSelectedNote(updatedNote);

        setNoteText(updatedNote.richText || "");
        setOriginalNoteText(updatedNote.richText || "");
        setEditableNoteCategory(
          (updatedNote.noteCategory as "clinical" | "non-clinical") ||
            "clinical"
        );
        setIsImportant(
          !!updatedNote.isImportant && updatedNote.isImportant === "yes"
        );
        const phoneCallStatus = updatedNote.isPhoneCall === "yes";
        setIsPhoneCall(phoneCallStatus);
        setOriginalIsPhoneCall(phoneCallStatus);

        if (updatedNote.type) {
          setSelectedNoteType(updatedNote.type);
          const noteType = props.noteTypeOptions.find(
            (opt) => opt.key === updatedNote.type
          );
          if (noteType) {
            setSelectedNoteLabel(noteType.value);
          }
        }

        const clinicianName =
          updatedNote.clinicianId?.name || updatedNote.clinician?.name || "-";
        setLastUpdatedBy(clinicianName);

        const createdByName = updatedNote.createdBy?.name;
        setCreatedBy(createdByName);
      }
    }
  }, [props.fetchedNotes, selectedNote, props.noteTypeOptions]);

  const handleUpdateNote = async (event: React.MouseEvent) => {
    event.preventDefault();

    if (!props.handleSaveNote || !selectedNote) return;

    const plainText = stripHtmlTags(noteText);
    if (!plainText.trim()) {
      return;
    }

    const currentNoteId = selectedNote._id;
    setIsUpdatingNote(true);
    try {
      const updatedNote: Partial<INote> = {
        ...selectedNote,
        richText: noteText,
        noteCategory: editableNoteCategory,
        isImportant: isImportant ? ("yes" as const) : ("no" as const),
        isPhoneCall: isPhoneCall ? "yes" : "no",
      };

      if (editableNoteCategory === "clinical") {
        updatedNote.type = selectedNoteType || ("general-note" as any);
      } else {
        delete updatedNote.type;
      }

      const response = await props.handleSaveNote(
        noteText,
        updatedNote as INote
      );

      await props.getNotes(true, "desc");

      type SaveNoteResponse = {
        data?: {
          note?: Array<INote>;
        };
      };

      const typedResponse = response as unknown as SaveNoteResponse;

      let freshNoteData: INote | undefined;

      if (typedResponse?.data?.note?.[0]) {
        freshNoteData = typedResponse.data.note[0];
      } else if (props.fetchedNotes && props.fetchedNotes.length > 0) {
        freshNoteData = props.fetchedNotes.find(
          (note) => note._id === currentNoteId
        );
      }

      if (freshNoteData) {
        setSelectedNote(freshNoteData);
        const clinicianName =
          freshNoteData.clinicianId?.name ||
          freshNoteData.clinician?.name ||
          "-";
        setLastUpdatedBy(clinicianName);

        const createdByName = freshNoteData.createdBy?.name;
        setCreatedBy(createdByName);
      }

      props.onCloseModal();
    } catch (error) {
      console.error("Error updating note:", error);
    } finally {
      setIsUpdatingNote(false);
    }
  };

  const modules = {
    toolbar: [
      ["bold", "italic"],
      [{ list: "ordered" }, { list: "bullet" }],
    ],
  };

  const formats = ["bold", "italic", "list", "bullet", "ordered"];

  return (
    <>
      <Modal
        show={props.ViewNotesModal.status}
        modalClassName={`${classes.container} ${classes.container_60}`}
      >
        <ModalHeading
          heading="View Notes"
          subHeading={`Patient Name: ${props.patientData?.name}`}
          customClass={classes.header}
          customCloseClass={classes.close_btn}
          closeIcon="close-cross"
          onClose={props.onCloseModal}
        />

        <div className={`${classes.inner_container} displayflex `}>
          <div className={`displayflex displayflexCol ${classes.left_section}`}>
            <div className="displayflex alignCenter justrifySpaceBetween">
              <h3 className={classes.sub_title}>Saved Notes</h3>

              <div className={classes.sortContainer}>
                <select
                  className={classes.sortSelect}
                  value={sortOrder}
                  onChange={handleSortChange}
                >
                  <option value="Latest First">Latest First</option>
                  <option value="Oldest First">Oldest First</option>
                </select>
              </div>
            </div>

            {/* Notes list */}
            <div
              className={classes.notes_list}
              ref={notesContainerRef}
              onScroll={handleScroll}
            >
              {props.getNotesLoading &&
              (!props.fetchedNotes || props.fetchedNotes.length === 0) ? (
                <NoteSkeleton count={3} />
              ) : props.fetchedNotes && props.fetchedNotes.length > 0 ? (
                props.fetchedNotes.map((note, index) => (
                  <div
                    key={note._id || index}
                    className={`${classes.note_card_small} ${
                      note.noteCategory === "clinical"
                        ? classes.note_card_clinical
                        : note.noteCategory === "non-clinical"
                        ? classes.note_card_non_clinical
                        : note.noteCategory === "timer-summary"
                        ? classes.note_card_timer_summary
                        : ""
                    } ${
                      selectedNote?._id === note._id
                        ? classes.note_card_selected
                        : ""
                    }`}
                    onClick={() => {
                      if (selectedNote?._id !== note._id) {
                        setSelectedNote(note);

                        setNoteText(note.richText || "");
                        setOriginalNoteText(note.richText || "");
                        setEditableNoteCategory(
                          note.noteCategory === "clinical" ||
                            note.noteCategory === "non-clinical"
                            ? note.noteCategory
                            : "clinical"
                        );
                      }
                    }}
                  >
                    <div className="displayflex gap-1 alignCenter justrifySpaceBetween">
                      <p className={classes.note_title}>
                        {note.isImportant === "yes" && (
                          <svg
                            className={classes.importantNoteIcon}
                            style={{ marginRight: "0.5rem" }}
                          >
                            <use xlinkHref="/assets/sprite.svg#grey-star-outline1" />
                          </svg>
                        )}

                        {note.noteCategory
                          ? note.noteCategory === "non-clinical"
                            ? "Internal Note"
                            : note.noteCategory === "timer-summary"
                            ? "Timer Summary"
                            : note.noteCategory.charAt(0).toUpperCase() +
                              note.noteCategory.slice(1) +
                              " Note"
                          : "N/A"}
                      </p>
                      <div className="displayflex gap-1 alignCenter">
                        <p className={classes.note_date}>
                          {formatDate(note.createdAt)}
                        </p>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteNoteModal({
                              status: true,
                              data: note,
                            });
                          }}
                          style={styles.iconButton}
                          disabled={deleteNoteLoading}
                          type="button"
                        >
                          <svg style={styles.iconDelete}>
                            <use xlinkHref="/assets/sprite.svg#icon-delete" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {note.noteCategory === "clinical" && note.type && (
                      <p className={classes.note_type_display}>
                        {note.type
                          .split("-")
                          .map(
                            (word) =>
                              word.charAt(0).toUpperCase() + word.slice(1)
                          )
                          .join(" ")}
                      </p>
                    )}
                    <div
                      className={`${classes.note_desc} ${classes.note_desc_truncate}`}
                      dangerouslySetInnerHTML={{ __html: note.richText || "" }}
                    />
                  </div>
                ))
              ) : (
                <div className={classes.no_notes}>
                  <p>No notes available for this patient</p>
                </div>
              )}

              {/* Loading indicator */}
              {props.getNotesLoading &&
                props.fetchedNotes &&
                props.fetchedNotes.length > 0 && (
                  <div className={classes.loadingMore}>
                    <div className={classes.loadingSpinner}></div>
                    <p>Loading more notes...</p>
                  </div>
                )}
            </div>
          </div>

          <div
            className={`displayflex displayflexCol ${classes.right_section}`}
          >
            <div className={classes.formSection}>
              <div className={classes.radioGroup}>
                <RadioButton
                  label="Clinical Notes"
                  name="noteTypeEdit"
                  checked={editableNoteCategory === "clinical"}
                  onChange={() => {
                    setEditableNoteCategory("clinical");
                    props.setNoteModalStates((prevVal) => ({
                      ...prevVal,
                      noteCategory: {
                        value: "clinical",
                        error: "",
                      },
                    }));
                  }}
                  disable={editableNoteCategory === "timer-summary"}
                />
                <RadioButton
                  label="Internal Notes"
                  name="noteTypeEdit"
                  checked={editableNoteCategory === "non-clinical"}
                  onChange={() => {
                    setEditableNoteCategory("non-clinical");
                    props.setNoteModalStates((prevVal) => ({
                      ...prevVal,
                      noteCategory: {
                        value: "non-clinical",
                        error: "",
                      },
                    }));
                  }}
                  disable={editableNoteCategory === "timer-summary"}
                />
                <RadioButton
                  label="Timer Summary"
                  name="noteTypeEdit"
                  checked={editableNoteCategory === "timer-summary"}
                  onChange={() => {
                    setEditableNoteCategory("timer-summary");
                    props.setNoteModalStates((prevVal) => ({
                      ...prevVal,
                      noteCategory: {
                        value: "timer-summary",
                        error: "",
                      },
                    }));
                  }}
                />
              </div>

              <div className={classes.formRow}>
                <div className={classes.noteTypeSelectContainer}>
                  <CustomDropdown
                    options={props.noteTypeOptions}
                    selectedValue={selectedNoteType}
                    selectedLabel={selectedNoteLabel}
                    onSelect={(key, label) => {
                      setSelectedNoteType(key);
                      setSelectedNoteLabel(label);
                    }}
                    placeholder="Select a Note Type"
                    disabled={
                      editableNoteCategory !== "clinical" ||
                      props.noteTypesLoading
                    }
                    loading={props.noteTypesLoading}
                    error={
                      props.noteTypesError ? "Failed to load note types" : ""
                    }
                  />
                </div>
                <div className={classes.flagCheckboxContainer}>
                  <label className={classes.flagCheckboxLabel}>
                    <input
                      type="checkbox"
                      className={classes.flagCheckbox}
                      checked={isImportant}
                      onChange={handleImportantChange}
                      disabled={deleteNoteLoading}
                    />
                    <span>Flag as important on report</span>
                  </label>
                </div>
              </div>
              <ReactQuill
                value={noteText}
                onChange={setNoteText}
                modules={modules}
                formats={formats}
                placeholder="Enter your notes here..."
              />

              <div className={classes.modalFooter}>
                <label className={classes.phoneCallLabel}>
                  <input
                    type="checkbox"
                    id="phoneCall"
                    className={classes.phoneCallCheckbox}
                    checked={isPhoneCall}
                    onChange={handlePhoneCallChange}
                    disabled={deleteNoteLoading || originalIsPhoneCall}
                  />
                  Phone Call
                </label>
                {selectedNote && (
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {createdBy && (
                      <div className={classes.lastUpdatedText}>
                        Created by: {createdBy}
                      </div>
                    )}
                    <div className={classes.lastUpdatedText}>
                      Updated by: {lastUpdatedBy || "-"}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className={classes.buttonContainer}>
              {selectedNote && (
                <Button
                  label="Add New"
                  type="button"
                  span={false}
                  variant="blue"
                  customClass={classes.btn}
                  onClick={handleClearForm}
                  disabled={
                    props.saveNoteLoading || isSavingNote || isUpdatingNote
                  }
                />
              )}
              <Button
                label={
                  selectedNote
                    ? isUpdatingNote
                      ? "Updating..."
                      : "Update Note"
                    : isSavingNote
                    ? "Saving..."
                    : "Save Note"
                }
                type="button"
                span={false}
                disabled={
                  props.saveNoteLoading || isSavingNote || isUpdatingNote
                }
                variant="blue"
                customClass={classes.btn}
                onClick={selectedNote ? handleUpdateNote : handleSaveNote}
              />
            </div>
          </div>
        </div>
      </Modal>

      <Modal show={deleteNoteModal.status}>
        <ModalHeading heading="Delete Note" />
        <ModalForm
          customClass={classes.deleteModalForm}
          onSubmit={handleDeleteConfirm}
        >
          <InputsSection customClass={classes.deleteInputSection}>
            <p className="primaryText">
              Are you sure you want to delete this note?
              <br />
              <br />
              <span className="dangerText">This action cannot be undone.</span>
            </p>
          </InputsSection>

          <ModalButtons
            submitButtonLabel="Delete Note"
            cancelButtonLabel="Cancel"
            onCancel={() => setDeleteNoteModal({ status: false })}
          />
        </ModalForm>
      </Modal>
    </>
  );
};

export default ViewNotesModal;
